import { DealPipelineInvestmentsQuery, DueDiligenceRecordStatus } from '@/__generated__/graphql';
import ActionDropdown, { IActionDropdownOption } from '@/components/basicComponents/actionDropdown';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { Loader } from '@/components/loaders/loader';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import {
  TableBodyWrapper,
  TableFixedCell,
  TableRowCell,
  TableScrollableCellsWrapper,
  TableWithHorizontalScroll,
  TableWithHorizontalScrollOuter,
  TableWithHorizontalScrollRowWrapper,
  TableWithHorizontalScrollWrapper,
  StickyTableHeaderClip,
  StickyTableHeaderInner,
  StickyTableHeaderWrapper,
  TextCell
} from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { TableColumns } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useStickyTableHeaderWithHorizontalScroll } from '@/hooks/useStickyTableHeaderWithHorizontalScroll';
import { dateFormatter, getUTCDate } from '@/shared/utils/date';
import { InvestmentPacingType, TenantType } from '@trace-8/trace8-schema';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { NavigateOptions } from 'react-router-dom';
import styled from 'styled-components';

const COLUMN_PADDING = 20; // Padding for cell content
const MIN_COLUMN_WIDTH = 100; // Minimum column width
const MAX_COLUMN_WIDTH = 350; // Maximum column width
const HEADER_FONT = '600 12px Blinker, serif'; // Font for header
const CELL_FONT = '600 16px Blinker, serif'; // Font for cell content (using 600 weight to account for worst case)

// Cached canvas context for measuring text width (created once, reused)
let cachedContext: CanvasRenderingContext2D | null = null;

const getCanvasContext = (): CanvasRenderingContext2D | null => {
  if (!cachedContext) {
    const canvas = document.createElement('canvas');
    cachedContext = canvas.getContext('2d');
  }
  return cachedContext;
};

// Utility function to measure text width using canvas
const measureTextWidth = (text: string, font: string): number => {
  const context = getCanvasContext();
  if (!context) return 0;
  context.font = font;
  return context.measureText(text).width;
};

const getAdvisorRestrictionsCellValue = (row: DealPipelineInvestmentsQuery['Investments']['data'][number]): string => {
  const { showToAllAdvisoryFirms, tenants } = row;

  if (showToAllAdvisoryFirms) {
    return 'Show All';
  }

  const advisoryFirms = tenants?.filter((tenant) => tenant.type === TenantType.ADVISORY_FIRM);

  if (advisoryFirms?.length && advisoryFirms?.length === 1) {
    return advisoryFirms[0]?.name || '';
  }

  if (advisoryFirms?.length && advisoryFirms.length > 0) {
    return `Multiple (${advisoryFirms?.length})`;
  }

  return 'No Advisor Visibility';
};

const getDueDiligenceCellValue = (row: DealPipelineInvestmentsQuery['Investments']['data'][number]): string => {
  const { dueDiligenceRecord } = row;

  if (!dueDiligenceRecord?.status) {
    return '--';
  }

  const funnelName = dueDiligenceRecord?.activeTier?.funnel?.latestFunnel?.name;
  switch (dueDiligenceRecord?.status) {
    case DueDiligenceRecordStatus.Active: {
      const tierName = dueDiligenceRecord?.activeTier?.name;
      return funnelName && tierName ? `${funnelName} - ${tierName}` : '--';
    }
    case DueDiligenceRecordStatus.NotStarted:
      return 'Not Started';
    case DueDiligenceRecordStatus.Approved: {
      return funnelName ? `${funnelName} - Approved` : 'Approved';
    }
    case DueDiligenceRecordStatus.Rejected: {
      return funnelName ? `${funnelName} - Rejected` : 'Rejected';
    }
    default:
      return '--';
  }
};

// Get cell text value for width calculation
// Get cell text value - single source of truth for cell content
const getCellTextValue = (row: DealPipelineInvestmentsQuery['Investments']['data'][number], cellKey: string): string => {
  switch (cellKey) {
    case 'name':
      return row?.name || '';
    case 'manager':
      return row?.managerId || '';
    case 'assetClass':
      return row?.subAssetClass ? `${row.assetClass.name} - ${row.subAssetClass.name}` : row?.assetClass?.name || '';
    case 'pacingType':
      return InvestmentPacingType[row?.pacingType] || '';
    case 'vintage':
      return row?.pacingStartingYear && row?.pacingStartingQuarter
        ? `${row?.pacingStartingYear?.toString() || ''}/${row?.pacingStartingQuarter?.toString() || ''}`
        : '';
    case 'investorDocsDue':
      return row?.investorDocsDue ? getUTCDate(new Date(row.investorDocsDue)) : '';
    case 'nextClose':
      return row?.nextClose ? getUTCDate(new Date(row.nextClose)) : '';
    case 'finalClose':
      return row?.finalClose ? getUTCDate(new Date(row.finalClose)) : '';
    case 'minimum':
      return row?.minimum?.toLocaleString() || '';
    case 'custodied':
      return typeof row?.custodied === 'boolean' ? (row?.custodied ? 'Yes' : 'No') : '';
    case 'ubtiBlocker':
      return typeof row?.ubtiBlocker === 'boolean' ? (row?.ubtiBlocker ? 'Yes' : 'No') : '';
    case 'sri':
      return typeof row?.sri === 'boolean' ? (row?.sri ? 'Yes' : 'No') : '';
    case 'targetIrr':
      return row?.targetIRR?.toLocaleString() || '';
    case 'targetYield':
      return row?.targetYield?.toLocaleString() || '';
    case 'aggregateInterest':
      return row?.aggregateInterest?.toLocaleString() || '';
    case 'aggregateCommitment':
      return row?.aggregateCommitment?.toLocaleString() || '';
    case 'assignee':
      return row?.assignee?.firstName && row?.assignee?.lastName ? `${row?.assignee?.firstName} ${row?.assignee?.lastName}` : '';
    case 'lastUpdate':
      return row?.updatedAt ? dateFormatter(format(new Date(row.updatedAt), 'yyyy-MM-dd'), 'yyyy-MM-dd') : '';
    case 'status':
      return row?.workflowInstance?.currentState?.label || '';
    case 'advisorRestrictions':
      return getAdvisorRestrictionsCellValue(row);
    case 'dueDiligence':
      return getDueDiligenceCellValue(row);
    default:
      return '';
  }
};

// Columns that use FormattingTooltip for number formatting
const TOOLTIP_COLUMNS = ['minimum', 'aggregateInterest', 'aggregateCommitment'];

export const dealPipelineTableCellValue = (
  row: DealPipelineInvestmentsQuery['Investments']['data'][number],
  column: TableColumns,
  openMobileModal?: (row: DealPipelineInvestmentsQuery['Investments']['data'][number], modalType: string) => void
) => {
  const { cellKey } = column;

  // // Special case: lastUpdate with custom component
  // if (cellKey === 'lastUpdate') {
  //   if (openMobileModal) {
  //     return (
  //       <MobileRowValue isRedirect onClick={() => openMobileModal(row, 'updates')}>
  //         {getCellTextValue(row, cellKey)}
  //       </MobileRowValue>
  //     );
  //   }
  //   return <LastUpdate row={row} initialDate={row.updatedAt} updatedAt={row.updatedAt} />;
  // }

  // Special case: advisorRestrictions with custom tooltip
  const advisoryFirms = row.tenants?.filter((tenant) => tenant.type === TenantType.ADVISORY_FIRM);
  if (cellKey === 'advisorRestrictions' && !row.showToAllAdvisoryFirms && advisoryFirms?.length > 1) {
    return (
      <TextCell>
        <Tooltip title={advisoryFirms?.map((tenant) => tenant.name).join(', ')}>{getCellTextValue(row, cellKey || '')}</Tooltip>
      </TextCell>
    );
  }

  // Columns with FormattingTooltip
  if (TOOLTIP_COLUMNS.includes(cellKey || '')) {
    const value = row?.[cellKey as keyof typeof row] as number | undefined;
    return (
      <TextCell>
        <FormattingTooltip disableTooltip={!value}>{value}</FormattingTooltip>
      </TextCell>
    );
  }

  // Default: simple text cell
  const textValue = getCellTextValue(row, cellKey || '');
  return <TextCell>{textValue || '--'}</TextCell>;
};

interface DealPipelineTableProps {
  columnsList: TableColumns[];
  tableRows: DealPipelineInvestmentsQuery['Investments']['data'];
  loading: boolean;
  animatingRowId: string | null;
  forceRecalculateTableFiltersHeight: number;
  openPage: (path: string, options?: NavigateOptions) => void;
  actionsByStatus: (row: DealPipelineInvestmentsQuery['Investments']['data'][number]) => IActionDropdownOption[];
}

export const DealPipelineTable = ({
  columnsList,
  tableRows,
  loading,
  animatingRowId,
  forceRecalculateTableFiltersHeight,
  openPage,
  actionsByStatus
}: DealPipelineTableProps) => {
  const { isTablet } = useResponsive();
  const { searchParams, onChangeSort } = useSearchParamsManager();
  const {
    bodyScrollRef,
    stickyHeaderInnerRef,
    fixedColumnRef,
    tableFiltersHeight,
    onBodyScroll
  } = useStickyTableHeaderWithHorizontalScroll({
    isTablet,
    forceRecalculateTableFiltersHeight
  });

  const key = searchParams.get('key') || 'LAST_UPDATED';
  const asc = searchParams.get('asc') === 'true' || false;

  // Calculate dynamic column widths based on content
  const columnsWithDynamicWidth = useMemo(() => {
    if (!tableRows.length) return columnsList;

    return columnsList.map((column) => {
      if (!column.cellKey) return column;

      // Measure header width
      const headerWidth = measureTextWidth(column.title, HEADER_FONT);

      if (column.isFixed) return column;

      // Measure max content width across all rows
      const maxContentWidth = tableRows.reduce((maxWidth, row) => {
        const cellText = getCellTextValue(row, column.cellKey!);
        const textWidth = measureTextWidth(cellText, CELL_FONT);
        return Math.max(maxWidth, textWidth);
      }, 0);

      // Calculate final width: max of header/content + padding, clamped between min and max
      const calculatedWidth = Math.max(headerWidth, maxContentWidth) + COLUMN_PADDING;
      const finalWidth = Math.min(Math.max(calculatedWidth, MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH);

      return {
        ...column,
        width: finalWidth
      };
    });
  }, [columnsList, tableRows]);

  // 10 is the gap (spacing) between table cells
  const totalTableWidth = columnsWithDynamicWidth.reduce((width, column) => (column.isHidden ? width : width + column.width + 10), 0);

  return (
    <TableWithHorizontalScrollOuter isTablet={isTablet}>
      <StickyTableHeaderWrapper tableFiltersHeight={tableFiltersHeight} isTablet={isTablet}>
        <StickyTableHeaderClip>
          <StickyTableHeaderInner ref={stickyHeaderInnerRef} $width={totalTableWidth}>
            <TableHeader
              columns={columnsWithDynamicWidth}
              savedSetSort={onChangeSort}
              savedSort={{ key, asc }}
              refetch={() => {}}
              forceRecalculateTableFiltersHeight={forceRecalculateTableFiltersHeight}
              withHorizontalScroll
              fixedColumnRef={fixedColumnRef}
            />
          </StickyTableHeaderInner>
        </StickyTableHeaderClip>
      </StickyTableHeaderWrapper>
      <TableWithHorizontalScrollWrapper ref={bodyScrollRef} onScroll={onBodyScroll} isTablet={isTablet}>
        <TableWithHorizontalScroll totalWidth={`${totalTableWidth}px`}>
          {loading ? (
            <LoaderWrapper>
              <Loader />
            </LoaderWrapper>
          ) : (
            <TableBodyWrapper>
              {!tableRows.length ? (
                <NoResults />
              ) : (
                <>
                  {tableRows.map((row) => {
                    const actionDropdownOptions = actionsByStatus(row);
                    return (
                      <TableWithHorizontalScrollRowWrapper key={row.id} loading={row.id === animatingRowId ? 1 : 0}>
                        <TableFixedCell id="fixedCell" width={columnsWithDynamicWidth[0].width} alignItems="center">
                          <CellWithActionDropdown>
                            <RedirectTitleCell onClick={() => openPage(`/strategyDetails/${encodeURIComponent(row.id)}?backToTitle=Manager Pipeline`)}>
                              {row.name}
                            </RedirectTitleCell>
                            {actionDropdownOptions.length ? (
                              <div>
                                <ActionDropdown options={actionDropdownOptions} />
                              </div>
                            ) : (
                              <></>
                            )}
                          </CellWithActionDropdown>
                        </TableFixedCell>
                        <TableScrollableCellsWrapper alignItems="center">
                          {columnsWithDynamicWidth
                            .filter((column) => !column.isFixed)
                            .map(
                              (column) =>
                                !column.isHidden && (
                                  <TableRowCell key={column.keySort} width={column.width} withHorizontalScroll>
                                    {dealPipelineTableCellValue(row, column)}
                                  </TableRowCell>
                                )
                            )}
                        </TableScrollableCellsWrapper>
                      </TableWithHorizontalScrollRowWrapper>
                    );
                  })}
                </>
              )}
            </TableBodyWrapper>
          )}
        </TableWithHorizontalScroll>
      </TableWithHorizontalScrollWrapper>
    </TableWithHorizontalScrollOuter>
  );
};

const LoaderWrapper = styled.div`
  position: absolute;
  left: 50%;
  margin-top: 80px;
`;

const CellWithActionDropdown = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
